/* istanbul ignore file */

import FormData from "form-data";
import { TaJson } from "ta-json";

import { MapStringTo, ReadableUploadSource } from "../base-types";
import { HEADERS } from "../constants/api";
import { FILE_UPLOAD } from "../constants/defaults";
import ErrorMessages from "../error-messages";
import { WebApiError } from "../errors/web-api-error";
import { FileVersion } from "../fileversion";
import Guard from "../guard";
import { CreateUploadResponse } from "../models/upload/create-upload-response";
import { UploadAction } from "../models/upload/upload-action";
import { UploadConfiguration } from "../models/upload/upload-configuration";
import { UploadRequest } from "../models/upload/upload-request";
import { UploadRequestWrapper } from "../models/upload/upload-request-wrapper";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { ResponseHandler } from "./response-handler";
import { ResponseMessage } from "./response-message";

/**
 * Client for uploading files (packages).
 */
export interface IUploadClient {
    /**
     * Attempts to upload the specified file.
     * @param request - Describes the file upload.
     * @returns The response of the file upload request.
     */
    uploadAsync(request: UploadRequest, cancelCallback?: CancelCallback): Promise<ResponseMessage>;
}

export class UploadClient implements IUploadClient {
    constructor(private client: IExtendedContentHubClient) {
        Guard.notNull(client);
    }

    async uploadAsync(request: UploadRequest, cancelCallback?: CancelCallback): Promise<ResponseMessage> {
        Guard.notNull(request);

        await this.client.versionChecker.compatibilityCheckAsync(new FileVersion(4, 0, 0, 0));

        const source = await request.source.getReadableSourceAsync();

        const uploadRequest = new UploadRequestWrapper();
        uploadRequest.fileName = request.source.name;
        uploadRequest.fileSize = source.byteLength;

        uploadRequest.uploadConfiguration = new UploadConfiguration(
            request.configurationName,
            request.configurationParameters
        );

        uploadRequest.action = new UploadAction(request.actionName, request.actionParameters);

        return await this.uploadFileAsync(uploadRequest, source, cancelCallback);
    }

    private async uploadFileAsync(
        uploadRequest: UploadRequestWrapper,
        uploadSource: ReadableUploadSource,
        cancelCallback?: CancelCallback
    ): Promise<ResponseMessage> {
        Guard.notNull(uploadRequest);
        Guard.notNull(uploadSource);

        // 1. Request new file to be created.

        const uploadLink = await this.client.linkHelper.createUploadToLinkAsync();
        const uploadResponse = await this.client.raw.postAsync(
            uploadLink.href,
            TaJson.serialize(uploadRequest),
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(uploadResponse);

        const uploadResponseLocation = uploadResponse.responseHeaders[HEADERS.location];

        if (!uploadResponseLocation) {
            throw new WebApiError(
                ErrorMessages.EntitiesClient.UnableToExtractLocationHeaderFromResponse,
                uploadResponse.statusCode,
                uploadResponse
            );
        }

        const createUploadResponse = TaJson.deserialize<CreateUploadResponse>(
            uploadResponse.content,
            CreateUploadResponse
        );

        // 2. Upload file

        if (uploadRequest.fileSize >= FILE_UPLOAD.chunkSize) {
            let chunkCurrent = 0;
            const chunkTotal = Math.ceil(uploadRequest.fileSize / FILE_UPLOAD.chunkSize);

            const chunkUploadUri = `${uploadResponseLocation}&chunks=${chunkTotal}`;

            let index = 0;
            while (index < uploadSource.byteLength) {
                const chunk = uploadSource.slice(index, FILE_UPLOAD.chunkSize + index);

                await this.uploadChunkAsync(
                    uploadRequest.fileName,
                    chunk,
                    `${chunkUploadUri}&chunk=${chunkCurrent}`,
                    cancelCallback
                );

                index += FILE_UPLOAD.chunkSize;
                chunkCurrent++;
            }

            // Finalize chunked upload
            const finalizeChunkUploadResponse = await this.client.raw.postAsync(
                chunkUploadUri,
                "",
                undefined,
                cancelCallback
            );

            ResponseHandler.handleErrors(finalizeChunkUploadResponse);
        } else {
            await this.uploadChunkAsync(uploadRequest.fileName, uploadSource, uploadResponseLocation, cancelCallback);
        }

        // 3. Finalize upload

        const finalizeUploadLink = await this.client.linkHelper.finalizeUploadToLinkAsync();

        const finalizeUploadResponse = await this.client.raw.postAsync(
            finalizeUploadLink.href,
            createUploadResponse,
            undefined,
            cancelCallback
        );

        ResponseHandler.handleErrors(finalizeUploadResponse);

        return finalizeUploadResponse;
    }

    private async uploadChunkAsync(
        fileName: string,
        buffer: ArrayBuffer,
        uri: string,
        cancelCallback?: CancelCallback
    ): Promise<void> {
        // Due to differences in the re-implementation of the FormData type in the Node.js wrapper we need two different paths to be able to handle uploads performed from Node.js and the browser.
        // See:
        // - https://github.com/form-data/form-data
        // - https://developer.mozilla.org/en-US/docs/Web/API/FormData

        const [form, headers] =
            typeof window === "undefined"
                ? this.prepareFormDataNode(fileName, buffer)
                : this.prepareFormDataBrowser(fileName, buffer);

        const uploadResponse = await this.client.raw.postAsync(uri, form, headers, cancelCallback);

        ResponseHandler.handleErrors(uploadResponse);
    }

    private prepareFormDataNode(fileName: string, buffer: ArrayBuffer): [FormData, MapStringTo<string>] {
        const form = new FormData({ autoDestroy: true });
        form.append("file", buffer, fileName);

        const headers = {
            "Content-Type": `multipart/form-data;boundary=${form.getBoundary()}`,
        };

        return [form, headers];
    }

    private prepareFormDataBrowser(fileName: string, buffer: ArrayBuffer): [FormData, MapStringTo<string>] {
        const form = new FormData();
        form.append("file", new Blob([buffer]), fileName);

        const headers = {
            "Content-Type": "multipart/form-data",
        };

        return [form, headers];
    }
}
